import { useState, useEffect } from "react";
import {
  MdPeople, MdBadge, MdNotifications, MdCalendarToday, MdEventNote,
  MdDescription, MdAssignment, MdBarChart, MdCheckCircle, MdCampaign,
  MdDownload, MdArrowBack, MdRefresh, MdSearch, MdVisibility, MdClose, MdCheck,
} from "react-icons/md";
import { reportAPI, departmentAPI, roleAPI, documentTypeAPI } from "../services/api";
import { Toast, errMessage, Pagination } from "../components/shared/index";
import { useLanguage } from "../context/LanguageContext";

const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const MUTED  = "var(--muted)";
const MO = "'Moul','Noto Sans Khmer',serif";
const KH = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";

const MONTHS = ["មករា","កុម្ភៈ","មីនា","មេសា","ឧសភា","មិថុនា","កក្កដា","សីហា","កញ្ញា","តុលា","វិច្ឆិកា","ធ្នូ"]
  .map((label, i) => ({ value: String(i + 1), label }));
const yr = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => { const y = String(yr - 2 + i); return { value: y, label: y }; });
const OFF_ST    = [{ value: "ACTIVE", label: "កំពុងបម្រើការ" }, { value: "INACTIVE", label: "អសកម្ម" }, { value: "SUSPENDED", label: "ផ្អាកការងារ" }, { value: "RETIRED", label: "ចូលនិវត្តន៍" }];
const USER_ST   = [{ value: "ACTIVE", label: "សកម្ម" }, { value: "INACTIVE", label: "អសកម្ម" }];
const MEET_ST   = [{ value: "SCHEDULED", label: "កំណត់" }, { value: "IN_PROGRESS", label: "កំពុងប្រជុំ" }, { value: "COMPLETED", label: "បានបញ្ចប់" }, { value: "CANCELLED", label: "លុបចោល" }];
const DOC_ST    = [{ value: "ACTIVE", label: "សកម្ម" }, { value: "EXPIRED", label: "ផុតកំណត់" }, { value: "ARCHIVED", label: "លុបបំបាត់" }];
const APPROV_ST = [{ value: "PENDING", label: "រង់ចាំ" }, { value: "APPROVED", label: "អនុម័ត" }, { value: "REJECTED", label: "បដិសេធ" }];
const ANN_ST    = [{ value: "DRAFT", label: "ព្រាង" }, { value: "PUBLISHED", label: "បោះពុម្ព" }, { value: "ARCHIVED", label: "លុបបំបាត់" }];
const ANN_PR    = [{ value: "LOW", label: "ទាប" }, { value: "NORMAL", label: "ធម្មតា" }, { value: "HIGH", label: "ខ្ពស់" }, { value: "URGENT", label: "បន្ទាន់" }];
const READ_ST   = [{ value: "true", label: "បានអាន" }, { value: "false", label: "មិនទាន់អាន" }];
const AUDIT_ACTIONS = [{ value: "CREATE", label: "CREATE" }, { value: "UPDATE", label: "UPDATE" }, { value: "DELETE", label: "DELETE" }, { value: "VIEW", label: "VIEW" }, { value: "LOGIN", label: "LOGIN" }];
const ENTITY_TYPES  = [{ value: "OFFICER", label: "មន្ត្រី" }, { value: "USER", label: "អ្នកប្រើ" }, { value: "DOCUMENT", label: "ឯកសារ" }, { value: "MEETING", label: "កិច្ចប្រជុំ" }, { value: "ANNOUNCEMENT", label: "ការប្រកាស" }, { value: "APPROVAL", label: "ការអនុម័ត" }];

const REPORTS = [
  { key: "users",            label: "អ្នកប្រើប្រាស់",   Icon: MdPeople,        color: "#6366F1" },
  { key: "officers",         label: "មន្ត្រី",            Icon: MdBadge,         color: BROWN     },
  { key: "notifications",    label: "ការជូនដំណឹង",       Icon: MdNotifications, color: "#F59E0B" },
  { key: "meetings",         label: "កិច្ចប្រជុំ",         Icon: MdCalendarToday, color: "#3B82F6" },
  { key: "meetingMinutes",   label: "លិខិតប្រជុំ",         Icon: MdEventNote,     color: "#8B5CF6" },
  { key: "documents",        label: "ឯកសារ",              Icon: MdDescription,   color: "#0EA5E9" },
  { key: "contractOfficers", label: "មន្ត្រីកិច្ចសន្យា",  Icon: MdAssignment,    color: "#10B981" },
  { key: "auditLogs",        label: "កំណត់ហេតុ",           Icon: MdBarChart,      color: "#64748B" },
  { key: "approvals",        label: "ការអនុម័ត",           Icon: MdCheckCircle,   color: "#22C55E" },
  { key: "announcements",    label: "ការប្រកាស",           Icon: MdCampaign,      color: "#EF4444" },
];

const DEFAULTS = {
  users:            { roleId: "", status: "" },
  officers:         { departmentId: "", status: "", from: "", to: "" },
  notifications:    { userId: "", type: "", isRead: "", from: "", to: "" },
  meetings:         { month: "", year: "", status: "" },
  meetingMinutes:   { month: "", year: "" },
  documents:        { userId: "", status: "", typeId: "", from: "", to: "" },
  contractOfficers: { expiringWithinDays: "30" },
  auditLogs:        { userId: "", action: "", entityType: "", from: "", to: "" },
  approvals:        { status: "", from: "", to: "" },
  announcements:    { status: "", priority: "", from: "", to: "" },
};

const EXPORT_FNS = {
  users:            reportAPI.exportUsers,
  officers:         reportAPI.exportOfficers,
  notifications:    reportAPI.exportNotifications,
  meetings:         reportAPI.exportMeetings,
  meetingMinutes:   reportAPI.exportMeetingMinutes,
  documents:        reportAPI.exportDocuments,
  contractOfficers: reportAPI.exportContractOfficers,
  auditLogs:        reportAPI.exportAuditLogs,
  approvals:        reportAPI.exportApprovals,
  announcements:    reportAPI.exportAnnouncements,
};

const PREVIEW_FNS = {
  users:            reportAPI.previewUsers,
  officers:         reportAPI.previewOfficers,
  notifications:    reportAPI.previewNotifications,
  meetings:         reportAPI.previewMeetings,
  meetingMinutes:   reportAPI.previewMeetingMinutes,
  documents:        reportAPI.previewDocuments,
  contractOfficers: reportAPI.previewContractOfficers,
  auditLogs:        reportAPI.previewAuditLogs,
  approvals:        reportAPI.previewApprovals,
  announcements:    reportAPI.previewAnnouncements,
};

const KH_COL = {
  no: "ល.រ", statusCode: "កូដស្ថានភាព", statusLabel: "ស្ថានភាព",
  createdAt: "កាលបរិច្ឆេទបង្កើត", departmentName: "នាយកដ្ឋាន",
  email: "អ៊ីមែល", phone: "ទូរស័ព្ទ", title: "ចំណងជើង",
  userNameKh: "ឈ្មោះអ្នកប្រើ (ខ្មែរ)", userNameEn: "ឈ្មោះអ្នកប្រើ (អង់គ្លេស)",
  roleName: "ឈ្មោះតួនាទី", roleDisplay: "តួនាទី", officerName: "ឈ្មោះមន្ត្រី",
  lastLoginAt: "ចូលប្រើចុងក្រោយ",
  officerCode: "កូដមន្ត្រី", contractOfficerCode: "កូដមន្ត្រីកិច្ចសន្យា",
  fullNameKh: "ឈ្មោះ (ខ្មែរ)", fullNameEn: "ឈ្មោះ (អង់គ្លេស)",
  genderLabel: "ភេទ", dob: "ថ្ងៃខែឆ្នាំកំណើត", age: "អាយុ",
  positionName: "មុខតំណែង", joinDate: "ថ្ងៃចូលបម្រើការ",
  educationLevel: "កម្រិតវប្បធម៌", jobLevel: "កម្រិតការងារ",
  accountingCode: "កូដគណនេយ្យ", startDate: "ថ្ងៃចាប់ផ្តើម", endDate: "ថ្ងៃបញ្ចប់",
  daysUntilExpiry: "ថ្ងៃនៅសល់", expiryLabel: "ស្ថានភាពផុតកំណត់", note: "កំណត់ចំណាំ",
  receiverName: "ឈ្មោះអ្នកទទួល", receiverEmail: "អ៊ីមែលអ្នកទទួល",
  message: "សារ", type: "កូដប្រភេទ", typeLabel: "ប្រភេទ",
  isRead: "បានអាន", readStatus: "ស្ថានភាពអាន", readAt: "ពេលអាន",
  announcementTitle: "ចំណងជើងប្រកាស",
  meetingType: "ប្រភេទប្រជុំ", meetingDate: "ថ្ងៃប្រជុំ",
  startTime: "ម៉ោងចាប់ផ្តើម", endTime: "ម៉ោងបញ្ចប់", roomCode: "កូដបន្ទប់",
  organizerName: "អ្នករៀបចំ", totalAttendees: "អ្នករួមចំណែកសរុប",
  attendedCount: "ចំនួនវត្តមាន", absentCount: "ចំនួនអវត្តមាន",
  meetingTitle: "ចំណងជើងប្រជុំ", meetingId: "លេខប្រជុំ", recordedBy: "កត់ត្រាដោយ",
  summary: "សេចក្តីសង្ខេប", decisions: "សេចក្តីសម្រេច", actionItems: "កិច្ចការត្រូវបន្ត",
  hasAttachment: "មានឯកសារភ្ជាប់",
  documentNumber: "លេខឯកសារ", documentName: "ឈ្មោះឯកសារ",
  documentTypeName: "ប្រភេទឯកសារ", userName: "ឈ្មោះអ្នកប្រើ",
  expiryDate: "ថ្ងៃផុតកំណត់", isExpired: "ផុតកំណត់", uploadedBy: "បញ្ចូលដោយ",
  requesterName: "អ្នកស្នើសុំ", requesterDept: "នាយកដ្ឋានអ្នកស្នើសុំ",
  approvedBy: "អនុម័តដោយ", comment: "មតិយោបល់",
  requestedAt: "ពេលស្នើសុំ", decidedAt: "ពេលសម្រេច",
  userEmail: "អ៊ីមែលអ្នកប្រើ", action: "កូដសកម្មភាព", actionLabel: "សកម្មភាព",
  entityType: "ប្រភេទទិន្នន័យ", entityId: "លេខទិន្នន័យ",
  details: "ព័ត៌មានលម្អិត", ipAddress: "អាសយដ្ឋាន IP",
  content: "ខ្លឹមសារ", createdBy: "បង្កើតដោយ", createdByDept: "នាយកដ្ឋានអ្នកបង្កើត",
  priority: "កូដអាទិភាព", priorityLabel: "អាទិភាព",
  publishAt: "ពេលផ្សព្វផ្សាយ", expireAt: "ពេលផុតកំណត់",
  totalRecipients: "អ្នកទទួលសរុប", readCount: "ចំនួនបានអាន",
  unreadCount: "ចំនួនមិនទាន់អាន", readRate: "អត្រាអាន",
};
const prettyCol = (k) =>
  KH_COL[k] ||
  k.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase()).trim();

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
const ISO_DATE_RE     = /^\d{4}-\d{2}-\d{2}$/;

const cellText = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean")
    return v ? <MdCheck size={14} color="#10B981" style={{ verticalAlign:"middle" }}/>
             : <MdClose size={14} color="#EF4444" style={{ verticalAlign:"middle" }}/>;
  if (typeof v === "string" && ISO_DATETIME_RE.test(v)) {
    const d = new Date(v);
    if (!isNaN(d)) return d.toLocaleString("en-GB", {
      day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit",
    });
  }
  if (typeof v === "string" && ISO_DATE_RE.test(v)) {
    const d = new Date(v);
    if (!isNaN(d)) return d.toLocaleDateString("en-GB", {
      day:"2-digit", month:"2-digit", year:"numeric",
    });
  }
  if (typeof v === "object")  return JSON.stringify(v);
  return String(v);
};

const inp = {
  padding: "8px 12px", border: `1px solid ${BORDER}`, borderRadius: 8,
  fontSize: 12, fontFamily: KH, color: "var(--text)", background:"var(--surface)",
  outline: "none", width: "100%", boxSizing: "border-box",
};

function SearchSel({ value, onChange, options }) {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [open,  setOpen]  = useState(false);

  const filtered = query
    ? options.filter(o => o.label?.toLowerCase().includes(query.toLowerCase()))
    : options;

  const selectedLabel = options.find(o => o.value === value)?.label ?? "";

  return (
    <div style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <MdSearch size={14} color={MUTED}
          style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
        <input
          value={open ? query : selectedLabel}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setQuery(""); setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={t("ស្វែងរក...")}
          style={{ ...inp, paddingLeft: 30 }}
        />
      </div>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          background:"var(--surface)", border: `1px solid ${BORDER}`, borderRadius: 8,
          zIndex: 200, maxHeight: 200, overflowY: "auto",
          boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
        }}>
          <div
            onMouseDown={() => { onChange(""); setQuery(""); setOpen(false); }}
            style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12, fontFamily: KH, color: MUTED, borderBottom: `1px solid ${BORDER}` }}
          >
            {t("ទាំងអស់")}
          </div>
          {filtered.length === 0
            ? <div style={{ padding: "8px 12px", fontSize: 12, fontFamily: KH, color: MUTED }}>{t("មិនមានលទ្ធផល")}</div>
            : filtered.map(o => (
              <div
                key={o.value}
                onMouseDown={() => { onChange(o.value); setQuery(""); setOpen(false); }}
                style={{
                  padding: "8px 12px", cursor: "pointer", fontSize: 12, fontFamily: KH,
                  color: "var(--text)", background: value === o.value ? "var(--cream)" : "var(--surface)",
                  fontWeight: value === o.value ? 700 : 400,
                }}
              >
                {o.label}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

function Sel({ value, onChange, options }) {
  const { t } = useLanguage();
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={inp}>
      <option value="">{t("ទាំងអស់")}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Input({ value, onChange, type = "text", placeholder }) {
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} style={inp} />
  );
}

function F({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ fontSize: 11, color: MUTED, fontFamily: KH, fontWeight: 600 }}>{label}</span>
      {children}
    </div>
  );
}

export default function Reports() {
  const { t } = useLanguage();
  const [view,      setView]      = useState("grid");
  const [selected,  setSelected]  = useState(null);
  const [exporting, setExporting] = useState(false);
  const [preview,    setPreview]    = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [page,       setPage]       = useState(0);
  const [pageSize,   setPageSize]   = useState(25);
  const [err,        setErr]        = useState(null);
  const [depts,     setDepts]     = useState([]);
  const [roles,     setRoles]     = useState([]);
  const [docTypes,  setDocTypes]  = useState([]);
  const [filters,   setFilters]   = useState(DEFAULTS);

  useEffect(() => {
    departmentAPI.getAll().then(r => {
      const d = r?.data?.data ?? r?.data;
      setDepts((Array.isArray(d) ? d : []).map(x => ({
        value: String(x.departmentId ?? x.id),
        label: x.departmentName ?? x.name,
      })));
    }).catch(() => {});

    roleAPI.getAll().then(r => {
      const d = r?.data?.data ?? r?.data;
      setRoles((Array.isArray(d) ? d : []).map(x => ({
        value: String(x.roleId ?? x.id),
        label: x.displayName ?? x.roleName ?? x.name,
      })));
    }).catch(() => {});

    documentTypeAPI.getAll().then(r => {
      const d = r?.data?.data ?? r?.data;
      setDocTypes((Array.isArray(d) ? d : []).map(x => ({
        value: String(x.id),
        label: x.name ?? x.nameKh,
      })));
    }).catch(() => {});
  }, []);

  function openFilter(key) {
    setSelected(key);
    setView("filter");
    setPreview(null);
  }

  function goBack() {
    setView("grid");
    setSelected(null);
    setPreview(null);
  }

  function set(field, value) {
    setFilters(f => ({ ...f, [selected]: { ...f[selected], [field]: value } }));
    setPreview(null);
  }

  function reset() {
    setFilters(f => ({ ...f, [selected]: { ...DEFAULTS[selected] } }));
    setPreview(null);
  }

  async function doExport() {
    setExporting(true);
    try { await EXPORT_FNS[selected](filters[selected]); }
    catch (e) { setErr("ទាញយកបរាជ័យ! " + errMessage(e)); }
    finally { setExporting(false); }
  }

  async function doPreview() {
    setPreviewing(true);
    try {
      const r = await PREVIEW_FNS[selected](filters[selected]);
      const d = r?.data?.data ?? r?.data;
      setPreview(Array.isArray(d) ? d : []);
      setPage(0);
    } catch (e) {
      setErr("មើលជាមុនបរាជ័យ! " + errMessage(e));
    } finally {
      setPreviewing(false);
    }
  }

  function renderFields() {
    const f = filters[selected];
    switch (selected) {

      case "users": return <>
        <F label={t("តួនាទី")}>
          <SearchSel value={f.roleId} onChange={v => set("roleId", v)} options={roles} />
        </F>
        <F label={t("ស្ថានភាព")}>
          <Sel value={f.status} onChange={v => set("status", v)} options={USER_ST} />
        </F>
      </>;

      case "officers": return <>
        <F label={t("នាយកដ្ឋាន")}>
          <SearchSel value={f.departmentId} onChange={v => set("departmentId", v)} options={depts} />
        </F>
        <F label={t("ស្ថានភាព")}>
          <Sel value={f.status} onChange={v => set("status", v)} options={OFF_ST} />
        </F>
        <F label={t("ពីថ្ងៃ")}>
          <Input value={f.from} onChange={v => set("from", v)} type="date" />
        </F>
        <F label={t("ដល់ថ្ងៃ")}>
          <Input value={f.to} onChange={v => set("to", v)} type="date" />
        </F>
      </>;

      case "notifications": return <>
        <F label={t("លេខអ្នកប្រើ")}>
          <Input value={f.userId} onChange={v => set("userId", v)} type="number" placeholder="ID" />
        </F>
        <F label={t("ប្រភេទ")}>
          <Input value={f.type} onChange={v => set("type", v)} placeholder={t("ប្រភេទការជូនដំណឹង")} />
        </F>
        <F label={t("ស្ថានភាព")}>
          <Sel value={f.isRead} onChange={v => set("isRead", v)} options={READ_ST} />
        </F>
        <F label={t("ពីថ្ងៃ")}>
          <Input value={f.from} onChange={v => set("from", v)} type="date" />
        </F>
        <F label={t("ដល់ថ្ងៃ")}>
          <Input value={f.to} onChange={v => set("to", v)} type="date" />
        </F>
      </>;

      case "meetings": return <>
        <F label={t("ខែ")}>
          <Sel value={f.month} onChange={v => set("month", v)} options={MONTHS} />
        </F>
        <F label={t("ឆ្នាំ")}>
          <Sel value={f.year} onChange={v => set("year", v)} options={YEARS} />
        </F>
        <F label={t("ស្ថានភាព")}>
          <Sel value={f.status} onChange={v => set("status", v)} options={MEET_ST} />
        </F>
      </>;

      case "meetingMinutes": return <>
        <F label={t("ខែ")}>
          <Sel value={f.month} onChange={v => set("month", v)} options={MONTHS} />
        </F>
        <F label={t("ឆ្នាំ")}>
          <Sel value={f.year} onChange={v => set("year", v)} options={YEARS} />
        </F>
      </>;

      case "documents": return <>
        <F label={t("លេខអ្នកប្រើ")}>
          <Input value={f.userId} onChange={v => set("userId", v)} type="number" placeholder="ID" />
        </F>
        <F label={t("ប្រភេទឯកសារ")}>
          <SearchSel value={f.typeId} onChange={v => set("typeId", v)} options={docTypes} />
        </F>
        <F label={t("ស្ថានភាព")}>
          <Sel value={f.status} onChange={v => set("status", v)} options={DOC_ST} />
        </F>
        <F label={t("ពីថ្ងៃ")}>
          <Input value={f.from} onChange={v => set("from", v)} type="date" />
        </F>
        <F label={t("ដល់ថ្ងៃ")}>
          <Input value={f.to} onChange={v => set("to", v)} type="date" />
        </F>
      </>;

      case "contractOfficers": return <>
        <F label={t("ផុតកំណត់ក្នុងរយៈ")} full>
          <Input value={f.expiringWithinDays} onChange={v => set("expiringWithinDays", v)} type="number" placeholder="30" />
        </F>
      </>;

      case "auditLogs": return <>
        <F label={t("លេខអ្នកប្រើ")}>
          <Input value={f.userId} onChange={v => set("userId", v)} type="number" placeholder="ID" />
        </F>
        <F label={t("សកម្មភាព")}>
          <Sel value={f.action} onChange={v => set("action", v)} options={AUDIT_ACTIONS} />
        </F>
        <F label={t("ប្រភេទ")}>
          <Sel value={f.entityType} onChange={v => set("entityType", v)} options={ENTITY_TYPES} />
        </F>
        <F label={t("ពីថ្ងៃ")}>
          <Input value={f.from} onChange={v => set("from", v)} type="date" />
        </F>
        <F label={t("ដល់ថ្ងៃ")}>
          <Input value={f.to} onChange={v => set("to", v)} type="date" />
        </F>
      </>;

      case "approvals": return <>
        <F label={t("ស្ថានភាព")}>
          <Sel value={f.status} onChange={v => set("status", v)} options={APPROV_ST} />
        </F>
        <F label={t("ពីថ្ងៃ")}>
          <Input value={f.from} onChange={v => set("from", v)} type="date" />
        </F>
        <F label={t("ដល់ថ្ងៃ")}>
          <Input value={f.to} onChange={v => set("to", v)} type="date" />
        </F>
      </>;

      case "announcements": return <>
        <F label={t("ស្ថានភាព")}>
          <Sel value={f.status} onChange={v => set("status", v)} options={ANN_ST} />
        </F>
        <F label={t("អាទិភាព")}>
          <Sel value={f.priority} onChange={v => set("priority", v)} options={ANN_PR} />
        </F>
        <F label={t("ពីថ្ងៃ")}>
          <Input value={f.from} onChange={v => set("from", v)} type="date" />
        </F>
        <F label={t("ដល់ថ្ងៃ")}>
          <Input value={f.to} onChange={v => set("to", v)} type="date" />
        </F>
      </>;

      default: return null;
    }
  }

  if (view === "grid") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text)", fontFamily: MO, margin: 0 }}>
            {t("របាយការណ៍")}
          </h2>
          <p style={{ fontSize: 12, color: MUTED, fontFamily: KH, margin: "4px 0 0" }}>
            {t("ជ្រើសរើសប្រភេទរបាយការណ៍ដែលអ្នកចង់ទាញយក")}
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14 }}>
          {REPORTS.map(r => (
            <div
              key={r.key}
              onClick={() => openFilter(r.key)}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = `0 6px 20px ${r.color}30`;
                e.currentTarget.style.borderColor = r.color;
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = BORDER;
                e.currentTarget.style.transform = "translateY(0)";
              }}
              style={{
                background:"var(--surface)",
                border: `2px solid ${BORDER}`,
                borderRadius: 12,
                padding: "22px 12px 18px",
                cursor: "pointer",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
                transition: "all 0.18s ease",
                userSelect: "none",
              }}
            >
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: `${r.color}15`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <r.Icon size={24} color={r.color} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, fontFamily: KH, color: "var(--text)", lineHeight: 1.5 }}>
                {r.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const activeReport = REPORTS.find(r => r.key === selected);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={goBack}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "7px 14px", border: `1px solid ${BORDER}`, borderRadius: 8,
            background:"var(--surface)", cursor: "pointer", fontSize: 12, fontFamily: KH, color: MUTED,
          }}
        >
          <MdArrowBack size={15} />
          {t("ត្រឡប់")}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: `${activeReport.color}15`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <activeReport.Icon size={17} color={activeReport.color} />
          </div>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--text)", fontFamily: MO, margin: 0 }}>
              របាយការណ៍{activeReport.label}
            </h2>
          </div>
        </div>
      </div>

      <div style={{
        background:"var(--surface)",
        border: `1px solid ${BORDER}`,
        borderTop: `3px solid ${activeReport.color}`,
        borderRadius: 12,
        padding: 28,
        display: "flex",
        flexDirection: "column",
        gap: 24,
      }}>

        <div>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", fontFamily: MO, margin: "0 0 4px" }}>
            {t("ជ្រើសរើសតម្រង")}
          </h4>
          <p style={{ fontSize: 11, color: MUTED, fontFamily: KH, margin: 0 }}>
            {t("ទុកទទេ ដើម្បីទាញយកទិន្នន័យទាំងអស់")}
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {renderFields()}
        </div>

        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          paddingTop: 20, borderTop: `1px solid ${BORDER}`,
        }}>
          <button
            onClick={reset}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 16px", border: `1px solid ${BORDER}`, borderRadius: 8,
              background:"var(--surface)", cursor: "pointer", fontSize: 12, fontFamily: KH, color: MUTED,
            }}
          >
            <MdRefresh size={15} />
            {t("លុបតម្រង")}
          </button>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={doPreview}
              disabled={previewing}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "10px 20px", borderRadius: 8,
                border: `1.5px solid ${activeReport.color}`,
                background:"var(--surface)", color: activeReport.color,
                cursor: previewing ? "not-allowed" : "pointer",
                fontSize: 13, fontFamily: KH, fontWeight: 700,
                opacity: previewing ? 0.6 : 1,
              }}
            >
              <MdVisibility size={17} />
              {previewing ? "កំពុងផ្ទុក..." : "មើលជាមុន"}
            </button>
            <button
              onClick={doExport}
              disabled={exporting}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "10px 28px", border: "none", borderRadius: 8,
                background: exporting ? "#B89A80" : activeReport.color,
                color: "#fff", cursor: exporting ? "not-allowed" : "pointer",
                fontSize: 13, fontFamily: KH, fontWeight: 700,
                transition: "opacity 0.2s",
              }}
            >
              <MdDownload size={17} />
              {exporting ? "កំពុងទាញ..." : "ទាញយករបាយការណ៍"}
            </button>
          </div>
        </div>
      </div>

      {preview && (
        <div style={{
          background:"var(--surface)", border: `1px solid ${BORDER}`,
          borderTop: `3px solid ${activeReport.color}`,
          borderRadius: 12, padding: 20,
        }}>
          <div style={{ display: "flex", alignItems: "center",
                        justifyContent: "space-between", marginBottom: 14 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: "var(--text)",
                         fontFamily: MO, margin: 0 }}>
              លទ្ធផលមើលជាមុន — {preview.length} ជួរ
            </h4>
            <button onClick={() => setPreview(null)}
              style={{ display: "flex", alignItems: "center", gap: 4,
                       background: "none", border: "none", cursor: "pointer",
                       color: MUTED, fontSize: 12, fontFamily: KH }}>
              <MdClose size={15} /> {t("បិទ")}
            </button>
          </div>

          {preview.length === 0 ? (
            <p style={{ fontSize: 12, color: MUTED, fontFamily: KH,
                        textAlign: "center", padding: "24px 0", margin: 0 }}>
              {t("គ្មានទិន្នន័យត្រូវនឹងតម្រង")}
            </p>
          ) : (
            <div style={{ overflowX: "auto", maxHeight: 420, overflowY: "auto",
                          border: `1px solid ${BORDER}`, borderRadius: 8 }}>
              <table style={{ borderCollapse: "collapse", width: "100%",
                              fontSize: 12, fontFamily: KH }}>
                <thead>
                  <tr>
                    {Object.keys(preview[0]).map(c => (
                      <th key={c} style={{
                        position: "sticky", top: 0, background: "var(--cream)",
                        color: BROWN, fontWeight: 700, textAlign: "left",
                        padding: "9px 12px", whiteSpace: "nowrap",
                        borderBottom: `2px solid ${BORDER}`,
                      }}>
                        {t(prettyCol(c))}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.slice(page * pageSize, page * pageSize + pageSize).map((row, i) => (
                    <tr key={i} style={{ background: i % 2 ? "var(--cream)" : "var(--surface)" }}>
                      {Object.keys(preview[0]).map(c => (
                        <td key={c} style={{
                          padding: "8px 12px", color: "var(--text)",
                          borderBottom: `1px solid ${BORDER}`,
                          whiteSpace: "nowrap", maxWidth: 280,
                          overflow: "hidden", textOverflow: "ellipsis",
                        }}>
                          {cellText(row[c])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.length > 0 && (
            <Pagination
              page={page}
              totalPages={Math.max(1, Math.ceil(preview.length / pageSize))}
              totalItems={preview.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={n => { setPageSize(n); setPage(0); }}
            />
          )}
        </div>
      )}
    </div>
  );
}
